/** @jsxImportSource @emotion/react */
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import {
  tableCellStyle,
  tableContainerStyle,
  tableHeaderCellStyle,
  tableHeadStyle,
  tableStyle,
} from './ArticleTable.style'

const tooltipProps = {
  tooltip: {
    sx: {
      fontSize: '0.75rem',
      maxWidth: '250px',
      textAlign: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  },
}

export const ArticleTable = ({ children, ...props }: { children: React.ReactNode }) => {
  const theme = useTheme()

  return (
    <TableContainer component={Paper} css={tableContainerStyle(theme)} {...props}>
      <Table css={tableStyle(theme)}>{children}</Table>
    </TableContainer>
  )
}

export const ArticleTableHead = ({ children, ...props }: { children: React.ReactNode }) => {
  const theme = useTheme()

  return (
    <TableHead css={tableHeadStyle(theme)} {...props}>
      {children}
    </TableHead>
  )
}

export const ArticleTableBody = ({ children, ...props }: { children: React.ReactNode }) => (
  <TableBody {...props}>{children}</TableBody>
)

export const ArticleTableRow = ({ children, ...props }: { children: React.ReactNode }) => (
  <TableRow {...props}>{children}</TableRow>
)

export const ArticleTableHeader = ({ children, ...props }: { children: React.ReactNode }) => {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const text = typeof children === 'string' ? children : String(children)

  useEffect(() => {
    if (cellRef.current) {
      const isOverflowing = cellRef.current.scrollHeight > cellRef.current.clientHeight
      setShowTooltip(isOverflowing)
    }
  }, [children])

  const cellContent = (
    <TableCell
      component="th"
      scope="col"
      ref={cellRef}
      css={tableHeaderCellStyle(showTooltip)}
      {...props}
    >
      {children}
    </TableCell>
  )

  return showTooltip ? (
    <Tooltip title={text} arrow placement="top" componentsProps={tooltipProps}>
      {cellContent}
    </Tooltip>
  ) : (
    cellContent
  )
}

export const ArticleTableCell = ({ children, ...props }: { children: React.ReactNode }) => {
  const theme = useTheme()

  return (
    <TableCell css={tableCellStyle(theme)} {...props}>
      {children}
    </TableCell>
  )
}
